/*
 * The Hygiene page (plan 2026-07-02-001, U6) — the "currently flagged" list.
 *
 * Lists every Ready-to-Launch ticket the hourly hygiene scan found broken:
 * the ticket (with its Notion link), product, the specific issues + fix hints,
 * the owners who were pinged, when it was first flagged, and the ping status
 * (pinged / ping failed—will retry / no owner—chase manually). Tickets drop off
 * automatically once fixed (the next scan clears them). "Scan now" enqueues an
 * immediate scan of the active brand (operator-gated).
 */

import { useState } from "react";
import { Anchor, Box, Group, Stack, Text } from "@mantine/core";
import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  Table,
  type Column,
} from "../components/ui";
import { TeachAliasModal } from "../components/aliases/TeachAliasModal";
import { AliasListModal } from "../components/aliases/AliasListModal";
import { useAuth } from "../auth/AuthContext";
import {
  useBookkeepDebts,
  useDismissBookkeepDebt,
  useHygieneFlags,
  useHygieneScanNow,
} from "../api/queries";
import { errorMessage } from "../api/errors";
import type { BookkeepDebtRow, HygieneFlagRow } from "../api/client";
import { formatDateTime } from "../lib/format";

/** Operator-friendly labels for the truth-debt kinds (batch 5 #8). */
const DEBT_KIND_LABELS: Record<string, string> = {
  sheet_no_row: "Sheet row missing",
  sheet_failed: "Sheet write failed",
  notion_mark_failed: "Notion mark failed",
  missing_page_id: "No Notion page to mark",
  adset_unmatched: "Adset stopped reporting",
};

/** The ping status a row renders — derived from the comment state. */
function pingStatus(flag: HygieneFlagRow): { label: string; tone: "ok" | "warn" | "dim" } {
  if (flag.owners.length === 0) return { label: "no owner — chase manually", tone: "warn" };
  if (flag.comment_error) return { label: "ping failed — will retry", tone: "warn" };
  if (flag.commented_at) return { label: "pinged", tone: "ok" };
  return { label: "ping pending", tone: "dim" };
}

export function HygienePage() {
  const { can } = useAuth();
  const flagsQuery = useHygieneFlags();
  const debtsQuery = useBookkeepDebts();
  const scanNow = useHygieneScanNow();
  const dismissDebt = useDismissBookkeepDebt();
  const [scanQueued, setScanQueued] = useState(false);
  // Teach-alias modal target (batch 5 #7) — a product+concept pair, opened from
  // a no-sheet-row flag OR a sheet_no_row debt row.
  const [teachTarget, setTeachTarget] = useState<{ productKey: string; concept: string } | null>(
    null,
  );
  const [aliasesOpen, setAliasesOpen] = useState(false);
  const [taughtConcept, setTaughtConcept] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const flags = flagsQuery.data?.flags ?? [];
  const debts = debtsQuery.data?.debts ?? [];

  const columns: Array<Column<HygieneFlagRow>> = [
    {
      key: "ticket",
      header: "Ticket",
      render: (flag) => (
        <>
          <Text component="span">{flag.concept ?? flag.notion_page_id}</Text>
          {flag.ticket_url && (
            <Anchor
              href={flag.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              fz="xs"
              ml={8}
            >
              Open
            </Anchor>
          )}
        </>
      ),
    },
    {
      key: "product",
      header: "Product",
      render: (flag) =>
        flag.product_key ? (
          flag.product_key
        ) : (
          <Text component="span" c="dimmed">
            —
          </Text>
        ),
      width: "90px",
    },
    {
      key: "issues",
      header: "Issues",
      render: (flag) => (
        <Stack gap={2}>
          {flag.issues.map((issue) => (
            <Text key={issue.code} fz="xs" title={issue.fix_hint}>
              • {issue.message}
            </Text>
          ))}
          {/* Batch 5 #7: a no-sheet-row flag is teachable in place — map the
              ticket concept to its real sheet row instead of renaming things. */}
          {can("operate") &&
            flag.product_key &&
            flag.concept &&
            flag.issues.some((i) => i.code === "no-sheet-row") && (
              <Box>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setTeachTarget({ productKey: flag.product_key!, concept: flag.concept! })
                  }
                >
                  Teach alias…
                </Button>
              </Box>
            )}
        </Stack>
      ),
    },
    {
      key: "owners",
      header: "Owners",
      render: (flag) =>
        flag.owners.length > 0 ? (
          <Text component="span" fz="xs">
            {flag.owners.map((o) => o.name ?? "?").join(", ")}
          </Text>
        ) : (
          <Text component="span" c="dimmed" fz="xs">
            none set
          </Text>
        ),
    },
    {
      key: "since",
      header: "Flagged since",
      render: (flag) => (
        <Text component="span" c="dimmed" fz="xs">
          {formatDateTime(flag.first_flagged_at)}
        </Text>
      ),
    },
    {
      key: "ping",
      header: "Ping",
      render: (flag) => {
        const s = pingStatus(flag);
        return (
          <Text
            component="span"
            fz="xs"
            role="note"
            c={s.tone === "warn" ? "orange" : s.tone === "ok" ? undefined : "dimmed"}
          >
            {s.label}
          </Text>
        );
      },
    },
  ];

  const debtColumns: Array<Column<BookkeepDebtRow>> = [
    {
      key: "kind",
      header: "What didn't land",
      render: (d) => (
        <Text component="span" fz="sm">
          {DEBT_KIND_LABELS[d.kind] ?? d.kind}
        </Text>
      ),
      width: "170px",
    },
    {
      key: "ref",
      header: "Concept / adset",
      render: (d) => (
        <Stack gap={2}>
          <Text component="span" fz="sm">
            {d.ref}
          </Text>
          {d.detail && (
            <Text component="span" c="dimmed" fz="xs">
              {d.detail}
            </Text>
          )}
        </Stack>
      ),
    },
    {
      key: "product",
      header: "Product",
      render: (d) => d.product_key,
      width: "90px",
    },
    {
      key: "attempts",
      header: "Tries",
      render: (d) => (
        <Text component="span" c="dimmed" fz="xs">
          {d.attempts}
        </Text>
      ),
      width: "50px",
    },
    {
      key: "since",
      header: "Since",
      render: (d) => (
        <Text component="span" c="dimmed" fz="xs">
          {formatDateTime(d.first_seen_at)}
        </Text>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (d) =>
        can("operate") ? (
          <Group gap={6} wrap="nowrap">
            {/* A missing sheet row is often really a naming drift — teach the
                alias and the hourly retry clears the debt by itself. */}
            {d.kind === "sheet_no_row" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setTeachTarget({ productKey: d.product_key, concept: d.ref })}
              >
                Teach alias…
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              loading={dismissDebt.isPending && dismissingId === d.id}
              onClick={() => {
                setDismissingId(d.id);
                dismissDebt.mutate(d.id);
              }}
            >
              Dismiss
            </Button>
          </Group>
        ) : null,
      width: "190px",
    },
  ];

  return (
    <>
      <PageHeader
        title="Hygiene"
        description="Ready-to-Launch tickets with problems the scanner flagged — fixed tickets clear on the next hourly scan."
        actions={
          <Group gap="sm">
            <Button variant="secondary" onClick={() => setAliasesOpen(true)}>
              Aliases
            </Button>
            {can("operate") && (
              <Button
                variant="primary"
                onClick={() =>
                  scanNow.mutate(undefined, { onSuccess: () => setScanQueued(true) })
                }
                disabled={scanNow.isPending}
              >
                {scanNow.isPending ? "Queuing…" : "Scan now"}
              </Button>
            )}
          </Group>
        }
      />

      {scanQueued && (
        <Box mb="md">
          <Alert tone="info">
            Scan queued — results land here as the worker finishes (typically under a
            minute per brand).
          </Alert>
        </Box>
      )}

      {taughtConcept && (
        <Box mb="md">
          <Alert tone="info">
            Alias taught for <strong>{taughtConcept}</strong> — the flag clears on
            the next scan (or press "Scan now").
          </Alert>
        </Box>
      )}

      {scanNow.isError && (
        <Box mb="md">
          <Alert tone="error">
            Couldn't queue the scan: {errorMessage(scanNow.error)}
          </Alert>
        </Box>
      )}

      {flagsQuery.isError && (
        <Box mb="md">
          <Alert tone="error">
            Couldn't load flagged tickets: {errorMessage(flagsQuery.error)}
          </Alert>
        </Box>
      )}

      {flagsQuery.isLoading ? (
        <EmptyState icon="◷" title="Loading flagged tickets…" />
      ) : flags.length === 0 ? (
        <EmptyState
          icon="⚑"
          title="No flagged tickets"
          description="Every Ready-to-Launch ticket passed the last hygiene scan."
        />
      ) : (
        <Group justify="space-between" align="flex-start">
          <Table columns={columns} rows={flags} rowKey={(flag) => flag.id} />
        </Group>
      )}

      {/* Truth-debt ledger (batch 5 #8) — rendered only when something is owed.
          An empty ledger IS the healthy state, so it draws nothing. */}
      {debtsQuery.isError && (
        <Box mt="md">
          <Alert tone="error">
            Couldn't load bookkeep debts: {errorMessage(debtsQuery.error)}
          </Alert>
        </Box>
      )}
      {debts.length > 0 && (
        <Box mt="xl">
          <Stack gap={4} mb="sm">
            <Text fw={600}>Bookkeep debts ({debts.length})</Text>
            <Text c="dimmed" fz="sm">
              Post-launch claims the app couldn't make true (sheet flips, Notion
              marks, adsets that stopped reporting). Retried hourly — rows clear
              when the mark lands. Dismiss what you've fixed by hand.
            </Text>
          </Stack>
          {dismissDebt.isError && (
            <Box mb="sm">
              <Alert tone="error">
                Couldn't dismiss the debt: {errorMessage(dismissDebt.error)}
              </Alert>
            </Box>
          )}
          <Table columns={debtColumns} rows={debts} rowKey={(d) => d.id} />
        </Box>
      )}

      <TeachAliasModal
        open={teachTarget !== null}
        productKey={teachTarget?.productKey ?? ""}
        concept={teachTarget?.concept ?? ""}
        onClose={() => setTeachTarget(null)}
        onTaught={() => setTaughtConcept(teachTarget?.concept ?? null)}
      />
      <AliasListModal open={aliasesOpen} onClose={() => setAliasesOpen(false)} />
    </>
  );
}
